/**
 * Scroll-pan layout renderer.
 * Makes the scene sticky, then animates an overlay character vertically
 * driven by scroll progress. An optional clipTop hides the character
 * above a certain percentage of the bg image (e.g. hide rope above cliff edge).
 *
 * Config: distance (vh), start.viewport (%), image { src, width, left, top, clipTop }.
 */

import { areAnimationsEnabled } from '../../../utils/animation-settings'
import { resolveSceneBg, getSceneImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import { getKeyframeValue, normalizeTransformOrigin } from '../animations/utils'
import type { SceneData } from '../types'

export interface ScrollPanRendererContext {
  container: HTMLDivElement
  wrapper: HTMLDivElement | null
  imageContainer: HTMLDivElement | null
  data: SceneData
  onImageContainerSet: (container: HTMLDivElement) => void
  onAppendSceneNumberDebug: () => void
}

type KeyframeEase = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'

interface ScrollPanKeyframe {
  at: number
  rotate?: number
  scale?: number
  ease?: KeyframeEase
}

interface ScrollPanConfig {
  distance: number
  viewport: number
  image: {
    src: string
    width: number
    left: number
    top: { from: number; to: number }
    clipTop?: number
    transformOrigin?: string
    keyframeEase?: KeyframeEase
    keyframes?: ScrollPanKeyframe[]
  }
}

function getConfig(data: SceneData): ScrollPanConfig {
  const cfg = data.layout?.config || {}
  const img = cfg.image!
  return {
    distance: cfg.distance ?? 5,
    viewport: cfg.start?.viewport ?? 50,
    image: {
      src: img.src,
      width: img.width,
      left: img.left,
      top: img.top,
      clipTop: img.clipTop,
      transformOrigin: img.transformOrigin,
      keyframeEase: img.keyframeEase,
      keyframes: img.keyframes?.slice().sort((a, b) => a.at - b.at),
    },
  }
}

export function renderScrollPan(context: ScrollPanRendererContext): HTMLElement {
  const { data } = context
  const config = getConfig(data)
  const dom = createDOM(context, config)

  const runAfterLoad = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setupScrollPanBehavior(context.wrapper, dom, data, config)
        context.onAppendSceneNumberDebug()
      })
    })
  }

  if (dom.bgImg.complete && dom.charImg.complete) runAfterLoad()
  else {
    let loaded = 0
    const total = (dom.bgImg.complete ? 0 : 1) + (dom.charImg.complete ? 0 : 1)
    const onLoad = () => {
      if (++loaded >= total) runAfterLoad()
    }
    if (!dom.bgImg.complete) dom.bgImg.addEventListener('load', onLoad, { once: true })
    if (!dom.charImg.complete) dom.charImg.addEventListener('load', onLoad, { once: true })
  }

  return dom.container
}

interface ScrollPanDOM {
  container: HTMLDivElement
  imageContainer: HTMLDivElement
  bgImg: HTMLImageElement
  charImg: HTMLImageElement
}

function createDOM(context: ScrollPanRendererContext, config: ScrollPanConfig): ScrollPanDOM {
  const { container, data, onImageContainerSet } = context

  const imageContainer = document.createElement('div')
  imageContainer.className = 'scene__image scene__image--scroll-pan'
  imageContainer.style.position = 'relative'
  imageContainer.style.width = '100%'
  onImageContainerSet(imageContainer)

  const bgPath = resolveSceneBg(data) ?? ''
  const bgImg = document.createElement('img')
  bgImg.src = bgPath
  bgImg.alt = data.id
  bgImg.className = 'scene__img'
  bgImg.style.width = '100%'
  bgImg.style.height = 'auto'
  bgImg.style.display = 'block'
  applyResponsiveImage(bgImg)
  imageContainer.appendChild(bgImg)

  const charImg = document.createElement('img')
  charImg.src = getSceneImagePath(data.id, config.image.src)
  charImg.alt = config.image.src.replace('.png', '')
  charImg.className = 'scroll-pan-character'
  charImg.style.position = 'absolute'
  charImg.style.width = `${config.image.width}%`
  charImg.style.height = 'auto'
  charImg.style.left = `${config.image.left - config.image.width / 2}%`
  charImg.style.top = `${config.image.top.from}%`
  if (config.image.transformOrigin) {
    charImg.style.transformOrigin = normalizeTransformOrigin(config.image.transformOrigin)
  }
  imageContainer.appendChild(charImg)

  container.appendChild(imageContainer)

  return { container, imageContainer, bgImg, charImg }
}

function setupScrollPanBehavior(
  wrapper: HTMLDivElement | null,
  dom: ScrollPanDOM,
  data: SceneData,
  config: ScrollPanConfig,
): void {
  if (!wrapper) return

  const { bgImg, charImg } = dom

  if (!areAnimationsEnabled()) {
    charImg.style.top = `${config.image.top.to}%`
    if (config.image.clipTop !== undefined) {
      const applyClip = () => {
        const bgH = bgImg.offsetHeight
        if (bgH <= 0) {
          requestAnimationFrame(applyClip)
          return
        }
        const clipLinePx = (config.image.clipTop! / 100) * bgH
        const charTopPx = (config.image.top.to / 100) * bgH
        const clipAmount = Math.max(0, clipLinePx - charTopPx)
        charImg.style.clipPath = clipAmount > 0 ? `inset(${clipAmount}px 0 0 0)` : 'none'
      }
      if (bgImg.complete) applyClip()
      else bgImg.addEventListener('load', applyClip, { once: true })
    }
    return
  }

  const vh = window.innerHeight
  const stickyDistance = vh * config.distance

  const stickyContainer = document.createElement('div')
  stickyContainer.className = 'scroll-pan-sticky-container'
  stickyContainer.style.width = '100%'
  stickyContainer.style.position = 'relative'

  const setupSticky = () => {
    const parentElement = wrapper.parentElement
    if (!parentElement) {
      requestAnimationFrame(setupSticky)
      return
    }

    const stickyTopPx = vh * (config.viewport / 100)
    wrapper.style.position = 'sticky'
    wrapper.style.top = `${stickyTopPx}px`
    wrapper.style.zIndex = '1'

    const updateDimensions = () => {
      const wrapperH = wrapper.getBoundingClientRect().height || wrapper.offsetHeight
      if (wrapperH > 0) {
        stickyContainer.style.height = `${wrapperH + stickyDistance}px`
      }
    }

    // Pre-calculate height before DOM shuffle to avoid layout shift
    const initialH = wrapper.getBoundingClientRect().height || wrapper.offsetHeight
    if (initialH > 0) {
      stickyContainer.style.height = `${initialH + stickyDistance}px`
    }

    parentElement.insertBefore(stickyContainer, wrapper)
    stickyContainer.appendChild(wrapper)

    let spacer = stickyContainer.nextElementSibling
    if (!spacer || !spacer.classList.contains('scene-spacer')) {
      const prevSibling = stickyContainer.previousElementSibling
      if (prevSibling?.classList.contains('scene-spacer')) {
        parentElement.insertBefore(prevSibling, stickyContainer.nextSibling)
      } else {
        spacer = document.createElement('div')
        spacer.className = 'scene-spacer'
        const nextSibling = stickyContainer.nextSibling
        if (nextSibling) parentElement.insertBefore(spacer, nextSibling)
        else parentElement.appendChild(spacer)
      }
    }

    // Update once synchronously, then schedule fallbacks for image load
    updateDimensions()
    if (!bgImg.complete) bgImg.addEventListener('load', updateDimensions, { once: true })
    requestAnimationFrame(() => {
      updateDimensions()
      setTimeout(updateDimensions, 200)
    })

    const getStickyStartScroll = (): number => {
      const containerRect = stickyContainer.getBoundingClientRect()
      return containerRect.top + window.scrollY
    }

    const originX = config.image.transformOrigin
      ? config.image.transformOrigin.trim().split(/\s+/)[0] || 'center'
      : 'center'

    const updateScrollProgress = () => {
      const scrollStartY = getStickyStartScroll()
      const relativeScroll = window.scrollY - scrollStartY
      const progress = Math.max(0, Math.min(1, relativeScroll / stickyDistance))

      const charTopPercent = config.image.top.from + (config.image.top.to - config.image.top.from) * progress
      charImg.style.top = `${charTopPercent}%`

      let clipAmount = 0
      if (config.image.clipTop !== undefined) {
        const bgH = bgImg.offsetHeight
        if (bgH > 0) {
          const clipLinePx = (config.image.clipTop / 100) * bgH
          const charTopPx = (charTopPercent / 100) * bgH
          clipAmount = Math.max(0, clipLinePx - charTopPx)
          charImg.style.clipPath = clipAmount > 0 ? `inset(${clipAmount}px 0 0 0)` : 'none'
        }
      }

      if (config.image.keyframes?.length) {
        if (config.image.clipTop !== undefined) {
          charImg.style.transformOrigin = `${originX} ${clipAmount}px`
        }
        const progressPercent = progress * 100
        const kfEase = config.image.keyframeEase
        const parts: string[] = []
        const rotate = getKeyframeValue(config.image.keyframes, progressPercent, 'rotate', kfEase)
        if (rotate !== undefined) parts.push(`rotate(${rotate}deg)`)
        const scale = getKeyframeValue(config.image.keyframes, progressPercent, 'scale', kfEase)
        if (scale !== undefined) parts.push(`scale(${scale})`)
        charImg.style.transform = parts.length ? parts.join(' ') : ''
      }
    }

    let scrollRafId: number | null = null
    const scrollHandler = () => {
      if (scrollRafId === null) {
        scrollRafId = requestAnimationFrame(() => {
          updateScrollProgress()
          scrollRafId = null
        })
      }
    }
    window.addEventListener('scroll', scrollHandler, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions()
      updateScrollProgress()
    })
    resizeObserver.observe(wrapper)
    resizeObserver.observe(stickyContainer)
    requestAnimationFrame(() => updateScrollProgress())
    ;(wrapper as any).__scrollPanCleanup = () => {
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)
      window.removeEventListener('scroll', scrollHandler)
      resizeObserver.disconnect()
      if (stickyContainer.parentElement && wrapper.parentElement === stickyContainer) {
        stickyContainer.parentElement.insertBefore(wrapper, stickyContainer)
      }
      if (stickyContainer.parentElement) {
        stickyContainer.parentElement.removeChild(stickyContainer)
      }
      wrapper.style.position = ''
      wrapper.style.top = ''
      wrapper.style.zIndex = ''
    }
  }

  setupSticky()
}
